import React, { useEffect, useState } from 'react';
import { FileBarChart2, Download, FileText, Users, GraduationCap, Stethoscope, ShieldCheck, HardHat, Loader2, Building2, AlertTriangle, Clock, Eye, UtensilsCrossed, Paperclip, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { useFetch, Spinner } from '../lib/useFetch';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { companiesApi, reportsApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { NotificheTab } from './ReportisticaPage';

const KpiBox = ({ label, value, bg, color }) => (
  <div className="rounded-2xl p-5" style={{ background: bg }}>
    <div className="text-[10px] uppercase tracking-widest font-semibold text-gray-600">{label}</div>
    <div className="mt-1 text-3xl font-extrabold" style={{ color }}>{value}</div>
  </div>
);

const ReportCard = ({ title, description, icon: Icon, accent, bg, onGenerate, busy, contentLine }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-start gap-4 hover:shadow-md transition-shadow">
    <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: bg, color: accent }}>
      <Icon size={22} />
    </div>
    <div className="flex-1 min-w-0">
      <h3 className="font-bold text-gray-800 text-sm">{title}</h3>
      <p className="text-xs text-gray-500 mt-1 leading-relaxed">{description}</p>
      {contentLine && (
        <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-full" style={{ background: bg, color: accent }}>
          <Eye size={11} /> Stai per scaricare: {contentLine}
        </div>
      )}
      <button
        onClick={onGenerate}
        disabled={busy}
        data-testid={`generate-${title.toLowerCase().replace(/\s+/g, '-').slice(0, 24)}`}
        className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold tracking-wider hover:opacity-90 transition-opacity disabled:opacity-50"
        style={{ background: accent, color: '#fff' }}
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} GENERA REPORT
      </button>
    </div>
  </div>
);

const PreviewWidget = ({ data, companyName, companyId }) => {
  if (!data) return null;
  const cidQuery = companyId && companyId !== 'all' ? `&companyId=${companyId}` : '';
  const blocks = [
    { key: 'formazione', label: 'Formazione', icon: GraduationCap, color: '#023F80', bg: '#e0e7ff', href: '/formazione' },
    { key: 'visite', label: 'Visite mediche', icon: Stethoscope, color: '#15803d', bg: '#dcfce7', href: '/sorveglianza' },
    { key: 'dpi', label: 'DPI', icon: HardHat, color: '#b45309', bg: '#fef7d4', href: '/dpi' },
    { key: 'haccp', label: 'HACCP', icon: UtensilsCrossed, color: '#9333ea', bg: '#f3e8ff', href: '/haccp' },
    { key: 'sicurezza', label: 'Sicurezza', icon: ShieldCheck, color: '#b91c1c', bg: '#fee2e2', href: '/sicurezza' },
  ];
  const totalRecords = blocks.reduce((acc, b) => acc + (data[b.key]?.total || 0), 0);
  const totalScaduti = blocks.reduce((acc, b) => acc + (data[b.key]?.scaduti || 0), 0);
  const totalSoon = blocks.reduce((acc, b) => acc + (data[b.key]?.in_scadenza_30gg || 0), 0);
  const hrefAll = `/formazione?status=scaduto${cidQuery}`;
  const hrefSoon = `/formazione?status=in_scadenza${cidQuery}`;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5" data-testid="report-preview">
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <div className="w-10 h-10 rounded-xl bg-[#fef7d4] text-[#8a6608] flex items-center justify-center">
          <Eye size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-gray-800 text-sm">Anteprima contenuti</h3>
          <p className="text-[11px] text-slate-500 truncate">
            {companyName ? `Ambito: ${companyName}` : 'Ambito: tutte le aziende del portafoglio'}
          </p>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <Link to={cidQuery ? `/aziende/${companyId}` : '/lavoratori'} className="px-2.5 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-[10px] font-bold uppercase">
            {data.workers} lavoratori
          </Link>
          {data.attachments > 0 && (
            <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase flex items-center gap-1">
              <Paperclip size={10} /> {data.attachments} allegati
            </span>
          )}
          {totalScaduti > 0 && (
            <Link to={hrefAll} className="px-2.5 py-1 rounded-full bg-red-100 hover:bg-red-200 text-red-800 text-[10px] font-bold uppercase flex items-center gap-1">
              <AlertTriangle size={10} /> {totalScaduti} scaduti
            </Link>
          )}
          {totalSoon > 0 && (
            <Link to={hrefSoon} className="px-2.5 py-1 rounded-full bg-amber-50 hover:bg-amber-100 text-amber-800 text-[10px] font-bold uppercase flex items-center gap-1">
              <Clock size={10} /> {totalSoon} ≤30gg
            </Link>
          )}
          <span className="px-2.5 py-1 rounded-full bg-[#023F80] text-white text-[10px] font-bold uppercase">
            {totalRecords} record totali
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {blocks.map((b) => {
          const Icon = b.icon;
          const v = data[b.key] || {};
          const isEmpty = !v.total;
          const baseHref = `${b.href}${cidQuery ? `?companyId=${companyId}` : ''}`;
          const scadHref = `${b.href}?status=scaduto${cidQuery}`;
          const soonHref = `${b.href}?status=in_scadenza${cidQuery}`;
          const Wrapper = isEmpty ? 'div' : Link;
          const wrapperProps = isEmpty ? {} : { to: baseHref };
          return (
            <Wrapper key={b.key} {...wrapperProps} className={`rounded-xl p-3 border block transition-colors ${isEmpty ? 'border-slate-100 bg-slate-50/60' : 'border-transparent hover:ring-2 hover:ring-offset-1 cursor-pointer'}`} style={!isEmpty ? { background: b.bg, '--tw-ring-color': b.color } : undefined}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <Icon size={14} style={{ color: isEmpty ? '#94a3b8' : b.color }} />
                <span className="text-[10px] uppercase font-bold tracking-wide" style={{ color: isEmpty ? '#94a3b8' : b.color }}>{b.label}</span>
              </div>
              <div className="text-2xl font-extrabold leading-tight" style={{ color: isEmpty ? '#94a3b8' : b.color }}>{v.total || 0}</div>
              {!isEmpty && (
                <div className="mt-1 text-[10px] text-slate-600 leading-tight space-y-0.5">
                  {v.scaduti > 0 && (
                    <Link to={scadHref} onClick={(e) => e.stopPropagation()} className="block text-red-700 font-semibold hover:underline">{v.scaduti} scaduti →</Link>
                  )}
                  {v.in_scadenza_30gg > 0 && (
                    <Link to={soonHref} onClick={(e) => e.stopPropagation()} className="block text-amber-700 font-semibold hover:underline">{v.in_scadenza_30gg} ≤30gg →</Link>
                  )}
                </div>
              )}
            </Wrapper>
          );
        })}
      </div>
    </div>
  );
};

const ReportPage = () => {
  const { user } = useAuth();
  const isClient = user?.role === 'client';
  const { data: companies, loading: lC } = useFetch(() => companiesApi.list(), []);
  const [companyId, setCompanyId] = useState(isClient ? user?.company : 'all');
  const [busy, setBusy] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    let active = true;
    setPreviewLoading(true);
    reportsApi.preview(companyId).then((res) => { if (active) setPreview(res.data); }).finally(() => { if (active) setPreviewLoading(false); });
    return () => { active = false; };
  }, [companyId]);

  if (lC) return <Spinner />;

  const selectedName = companyId === 'all'
    ? 'Tutte le aziende'
    : (companies || []).find((c) => c.id === companyId)?.name || preview?.company?.name || companyId;

  const run = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
      toast.success('Download avviato');
    } catch (e) { if (e !== 'skip') toast.error(e.response?.data?.detail || 'Errore download'); }
    finally { setBusy(null); }
  };

  const downloadExcel = () => companyId === 'all' ? reportsApi.bulkExcel() : reportsApi.companyExcel(companyId);
  const downloadZip = () => {
    if (companyId === 'all') {
      toast.error("Il pacchetto ispezione richiede di selezionare un'azienda");
      return Promise.reject('skip');
    }
    return reportsApi.companyZip(companyId);
  };

  // Content lines for each card
  const totalAllRecords = preview ? ['formazione', 'visite', 'dpi', 'haccp', 'sicurezza'].reduce((acc, k) => acc + (preview[k]?.total || 0), 0) : 0;
  const lineExcel = preview ? `${totalAllRecords} scadenze in 5 fogli` : null;
  const lineZip = preview ? `${totalAllRecords} scadenze + ${preview.attachments || 0} allegati` : null;
  const lineFormazione = preview?.formazione ? `${preview.formazione.total} corsi (${preview.formazione.scaduti} scaduti)` : null;
  const lineVisite = preview?.visite ? `${preview.visite.total} visite (${preview.visite.scaduti} scadute)` : null;
  const lineDpi = preview?.dpi ? `${preview.dpi.total} DPI` : null;
  const lineDocs = preview ? `${(preview.haccp?.total || 0) + (preview.sicurezza?.total || 0)} documenti + ${preview.attachments || 0} allegati` : null;

  return (
    <div className="space-y-5" data-testid="report-page">
      <Tabs defaultValue="download" className="w-full">
        <TabsList className="bg-slate-100 p-1 flex-wrap h-auto" data-testid="report-tabs">
          <TabsTrigger value="download" data-testid="tab-download" className="data-[state=active]:bg-[#023F80] data-[state=active]:text-white text-slate-700 rounded-lg px-4 py-2 text-xs font-semibold flex items-center gap-2">
            <Download size={14} /> Scarica report
          </TabsTrigger>
          {!isClient && (
            <TabsTrigger value="notifiche" data-testid="tab-notifiche-email" className="data-[state=active]:bg-[#023F80] data-[state=active]:text-white text-slate-700 rounded-lg px-4 py-2 text-xs font-semibold flex items-center gap-2">
              <Mail size={14} /> Notifiche email
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="download" className="mt-5 space-y-5">
          <div className="bg-gradient-to-r from-[#e0e7ff] to-[#dcfce7] rounded-2xl p-6 flex items-center gap-5 flex-wrap">
            <div className="w-14 h-14 rounded-2xl bg-white text-[#023F80] flex items-center justify-center"><FileBarChart2 size={28} /></div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-bold text-gray-800">Centro Report</h2>
              <p className="text-sm text-gray-600 mt-1">Esporta in Excel tutte le scadenze (formazione, visite, DPI, HACCP, sicurezza) per una singola azienda o per l'intero portafoglio.</p>
            </div>
          </div>

      {!isClient && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4 flex-wrap">
          <div className="w-10 h-10 rounded-xl bg-[#e0e7ff] text-[#023F80] flex items-center justify-center"><Building2 size={18} /></div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-gray-800">Ambito del report</h3>
            <p className="text-xs text-slate-500">Seleziona l'azienda per vedere l'anteprima dei contenuti. "Tutte le aziende" abilita solo il riepilogo Excel.</p>
          </div>
          <Select value={companyId} onValueChange={setCompanyId}>
            <SelectTrigger data-testid="report-company-select" className="min-w-[260px] h-10"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-80">
              <SelectItem value="all">Tutte le aziende</SelectItem>
              {(companies || []).filter((c) => c.id).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {previewLoading ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6"><Spinner /></div>
      ) : (
        <PreviewWidget data={preview} companyName={preview?.company?.name || (companyId === 'all' ? null : selectedName)} companyId={companyId} />
      )}

      <KpiRow preview={preview} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ReportCard
          title={`Riepilogo Excel — ${selectedName}`}
          description="File XLSX multi-foglio: Formazione, Visite, DPI, HACCP, Sicurezza."
          icon={FileText} accent="#023F80" bg="#e0e7ff"
          onGenerate={() => run('excel', downloadExcel)} busy={busy === 'excel'}
          contentLine={lineExcel}
        />
        <ReportCard
          title={companyId === 'all' ? 'Pacchetto ispezione (richiede azienda)' : `Pacchetto ispezione — ${selectedName}`}
          description="ZIP con Excel scadenze + tutti i file caricati. Pronto per gli organi di vigilanza."
          icon={ShieldCheck} accent="#b91c1c" bg="#fee2e2"
          onGenerate={() => run('zip', downloadZip)} busy={busy === 'zip'}
          contentLine={companyId === 'all' ? null : lineZip}
        />
        <ReportCard
          title="Scadenzario formazione"
          description="Corsi con date di rilascio, scadenza e stato (incluso nell'Excel riepilogo)."
          icon={GraduationCap} accent="#1e40af" bg="#dcfce7"
          onGenerate={() => run('excel', downloadExcel)} busy={busy === 'excel'}
          contentLine={lineFormazione}
        />
        <ReportCard
          title="Sorveglianza sanitaria"
          description="Visite mediche per lavoratore con idoneità e prossime scadenze."
          icon={Stethoscope} accent="#8a6608" bg="#fef7d4"
          onGenerate={() => run('excel', downloadExcel)} busy={busy === 'excel'}
          contentLine={lineVisite}
        />
        <ReportCard
          title="Documenti sicurezza"
          description="DVR, piani di emergenza, sicurezza, HACCP. I PDF allegati sono nel pacchetto ispezione."
          icon={ShieldCheck} accent="#b91c1c" bg="#fee2e2"
          onGenerate={() => run('zip', downloadZip)} busy={busy === 'zip'}
          contentLine={companyId === 'all' ? null : lineDocs}
        />
        <ReportCard
          title="Registro DPI"
          description="Consegne DPI e relative scadenze (foglio dedicato nell'Excel riepilogo)."
          icon={HardHat} accent="#8a6608" bg="#fef7d4"
          onGenerate={() => run('excel', downloadExcel)} busy={busy === 'excel'}
          contentLine={lineDpi}
        />
      </div>

      <div className="bg-[#fef7d4]/60 rounded-2xl border border-[#FFCB05]/40 p-5">
        <h3 className="font-bold text-gray-800 mb-2 text-sm flex items-center gap-2"><Users size={16} /> Hai bisogno di funzionalità avanzate?</h3>
        <p className="text-xs text-slate-700">
          Vai su <Link to="/operazioni-massive" className="font-bold text-[#023F80] hover:underline">Operazioni Massive</Link> per programmare corsi e visite mediche per più lavoratori in un solo click, oppure usa la scheda <strong>Notifiche email</strong> qui sopra per impostare reminder automatici delle scadenze.
        </p>
      </div>
        </TabsContent>

        {!isClient && (
          <TabsContent value="notifiche" className="mt-5">
            <NotificheTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

const KpiRow = ({ preview }) => {
  if (!preview) return null;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <KpiBox label="Lavoratori" value={preview.workers || 0} bg="#e0e7ff" color="#023F80" />
      <KpiBox label="Corsi formazione" value={preview.formazione?.total || 0} bg="#dcfce7" color="#1e40af" />
      <KpiBox label="Visite mediche" value={preview.visite?.total || 0} bg="#fef7d4" color="#8a6608" />
      <KpiBox label="Documenti sicurezza" value={(preview.haccp?.total || 0) + (preview.sicurezza?.total || 0)} bg="#fee2e2" color="#b91c1c" />
    </div>
  );
};

export default ReportPage;
